import { Controller } from '@hotwired/stimulus'
import {
  computePosition,
  autoUpdate,
  autoPlacement,
  flip,
  shift,
  offset,
  arrow,
  size
} from '@floating-ui/dom'
import { wrapViewTransition } from '../helpers/view_transitions'

const OFFSET = 8 // px
const ARROW_OFFSET = -4 // px

// Connects to data-controller="tooltip"
export default class extends Controller {
  static targets = ['trigger', 'tooltip', 'arrow']
  static values = {
    position: { type: String },
    alwaysShow: { type: Boolean, default: false },
    hideTimeout: { type: Number, default: 0 },
    showTimeout: { type: Number, default: 400 },
    interactive: { type: Boolean, default: false },
    suppressed: { type: Boolean, default: false }
  }

  set reference (reference) {
    this._reference = reference
  }

  get reference () {
    return this._reference || this.triggerTarget
  }

  connect () {
    this.intentToShow = false
    this.cleanupPosition = () => {}
    this.tooltipContentHash = null
    this.tooltipElement = null
    this.abortController = null
    this.timers = {}
    this.isTouchDevice = window.matchMedia('(hover: none)').matches && navigator.maxTouchPoints > 0

    // In case of Turbo Morph event, we need to portal out the tooltip
    // element and declare the event listeners again
    this.onMorph = () => {
      this.teardown()
      this.setup()
    }
    this.onBeforeRender = () => {
      if (!this.tooltipElement) return

      // We "portal in" the element back to the original parent before render/cache
      // so it can start all over again when the page is restored
      this.element.appendChild(this.tooltipElement)
      this.hide({ immediate: true })
    }

    document.addEventListener('turbo:morph', this.onMorph)
    document.addEventListener('turbo:before-render', this.onBeforeRender)
    this.setup()
  }

  disconnect () {
    document.removeEventListener('turbo:morph', this.onMorph)
    document.removeEventListener('turbo:before-render', this.onBeforeRender)
    this.teardown()
  }

  suppressedValueChanged (suppressed) {
    if (!suppressed || !this.tooltipElement) return

    this.hide({ immediate: true })
  }

  hide ({ immediate = false } = {}) {
    this.intentToShow = false
    this.cancelTimers()
    this.cleanupPosition()

    const applyHide = () => {
      if (!this.tooltipElement) return
      this.tooltipElement.setAttribute('hidden', '')
      this.setMeasuring(false)
    }

    if (immediate || !this.isVisible()) {
      applyHide()
      return
    }

    window.requestAnimationFrame(() => wrapViewTransition(applyHide))
  }

  toggle () {
    if (this.suppressedValue) return

    this.isVisible() ? this.hide() : this.show()
  }

  setup () {
    if (!this.hasTooltipTarget) return

    this.tooltipElement = this.tooltipTarget.cloneNode(true)
    this.tooltipElement.setAttribute('hidden', '')

    // We "portal out" a clone of the element to the end of the body/open modal dialog to avoid z-index and clipping issues
    this.portalParent().appendChild(this.tooltipElement)

    if (!this.hasTriggerTarget) return

    this.abortController = new AbortController()
    const { signal } = this.abortController

    // Mouse pointers still move on headless CI, which reports (hover: none).
    // Touch pointers are ignored here and use click below.
    const hoverIn = (event) => {
      if (event.pointerType === 'touch') return
      this.hoverIn()
    }
    const hoverOut = (event) => {
      if (event.pointerType === 'touch') return
      this.hoverOut()
    }
    for (const element of [this.triggerTarget, this.tooltipElement]) {
      element.addEventListener('pointerenter', hoverIn, { signal })
      element.addEventListener('pointerleave', hoverOut, { signal })
    }

    if (this.isTouchDevice) {
      this.triggerTarget.addEventListener('click', () => this.toggle(), { signal })
      const dismiss = (event) => {
        if (!this.isVisible()) return
        if (this.triggerTarget.contains(event.target)) return
        if (this.tooltipElement.contains(event.target)) return
        this.hide()
      }
      document.addEventListener('click', dismiss, { signal })
      document.addEventListener('touchstart', dismiss, { signal })
    }

    if (this.interactiveValue) {
      for (const eventName of ['mousedown', 'click']) {
        this.triggerTarget.addEventListener(eventName, (event) => event.stopPropagation(), { signal })
        this.tooltipElement.addEventListener(eventName, (event) => {
          event.stopPropagation()
          this.intentToShow = true
        }, { signal })
      }
    }
  }

  teardown () {
    this.intentToShow = false
    this.cancelTimers()
    // Removes all listeners registered with { signal } in setup()
    this.abortController?.abort()
    this.abortController = null
    this.cleanupPosition()
    this.tooltipElement?.remove()
    this.tooltipElement = null
  }

  hoverIn () {
    if (this.suppressedValue) return

    this.intentToShow = true
    this.cancelTimer('hide')
    if (this.isVisible() || this.timers.show) return
    this.show()
  }

  hoverOut () {
    this.intentToShow = false
    this.scheduleTimer('hide', () => {
      if (this.intentToShow || this.pointerOverTrigger()) {
        this.intentToShow = true
        return
      }
      this.hide()
    }, this.hideTimeoutValue)
  }

  show () {
    if (this.suppressedValue) return

    this.intentToShow = true
    this.cancelTimers()
    this.syncContent()

    if (this.showTimeoutValue === 0) {
      this.reveal()
      return
    }

    this.scheduleTimer('show', () => {
      if (this.intentToShow) this.reveal()
    }, this.showTimeoutValue)
  }

  async reveal () {
    if (!this.intentToShow || !this.tooltipElement) return

    // Compute the position of the tooltip while invisible (not display:none) so Floating UI can measure it
    this.setMeasuring(true)
    await this.updatePosition()

    if (!this.intentToShow && !this.pointerOverTrigger()) {
      this.setMeasuring(false)
      this.tooltipElement.setAttribute('hidden', '')
      return
    }

    this.intentToShow = true
    this.tooltipElement.removeAttribute('hidden')
    this.setMeasuring(false)
    this.cleanupPosition = autoUpdate(this.reference, this.tooltipElement, () => this.updatePosition())
  }

  syncContent () {
    // Update the clone's content from the original element
    const html = this.tooltipTarget.innerHTML
    if (html === this.tooltipContentHash) return

    this.tooltipContentHash = html
    this.tooltipElement.innerHTML = html
  }

  setMeasuring (active) {
    if (active) this.tooltipElement.removeAttribute('hidden')

    Object.assign(this.tooltipElement.style, {
      visibility: active ? 'hidden' : '',
      pointerEvents: (active || !this.interactiveValue) ? 'none' : ''
    })
  }

  updatePosition () {
    return computePosition(this.reference, this.tooltipElement, this.positionOptions())
      .then((data) => this.applyStyles(data))
  }

  positionOptions () {
    const tooltipElement = this.tooltipElement
    const middleware = [
      this.positionValue === 'auto' ? autoPlacement() : flip(),
      shift({ padding: OFFSET }),
      offset(OFFSET),
      ...(this.isTouchDevice
        ? [size({
            padding: OFFSET,
            apply ({ availableWidth }) {
              Object.assign(tooltipElement.style, { maxWidth: `${availableWidth}px` })
            }
          })]
        : []),
      arrow({ element: this.arrowElement() })
    ]

    return { placement: this.positionValue, strategy: 'fixed', middleware }
  }

  applyStyles ({ x, y, placement, middlewareData }) {
    Object.assign(this.tooltipElement.style, { top: `${y}px`, left: `${x}px` })

    const { x: arrowX, y: arrowY } = middlewareData.arrow
    const staticSide = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' }[placement.split('-')[0]]

    Object.assign(this.arrowElement().style, {
      left: arrowX != null ? `${arrowX}px` : '',
      top: arrowY != null ? `${arrowY}px` : '',
      right: '',
      bottom: '',
      [staticSide]: `${ARROW_OFFSET}px`
    })
  }

  isVisible () {
    return this.tooltipElement && !this.tooltipElement.hasAttribute('hidden')
  }

  portalParent () {
    return document.querySelector('dialog[open]:modal') || document.body
  }

  scheduleTimer (name, callback, delay) {
    this.cancelTimer(name)
    this.timers[name] = window.setTimeout(() => {
      this.timers[name] = null
      callback()
    }, delay)
  }

  cancelTimer (name) {
    if (!this.timers[name]) return
    window.clearTimeout(this.timers[name])
    this.timers[name] = null
  }

  cancelTimers () {
    for (const name of ['show', 'hide']) this.cancelTimer(name)
  }

  arrowElement () {
    return this.tooltipElement.querySelector('[data-tooltip-target="arrow"]')
  }

  pointerOverTrigger () {
    return this.hasTriggerTarget && this.triggerTarget.matches(':hover')
  }
}
